import { cn, convertNumber } from '@/helpers/utils/functionality-utils';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { staffDActions } from '@/store/slices/main/dashboard/staffDashboardMyTaskSlice';
import { format, isToday } from 'date-fns';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { useIntersectionObserver } from 'primereact/hooks';
import { ScrollPanel } from 'primereact/scrollpanel';
import { useEffect, useRef, type RefObject } from 'react';
import { Link } from 'react-router';
import { RemoveScroll } from 'react-remove-scroll';
import type { OverlayPanel } from 'primereact/overlaypanel';

export const TasksAsPopover = ({
  isFetching,
  overlayRef,
}: {
  isFetching: boolean;
  overlayRef: RefObject<OverlayPanel | null>;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('tasks');

  const userPermissions = useAppSelector(
    (state) => state.user.authUserData.userPermissions,
  );

  const cachedTasks = useAppSelector(
    (state) => state.staffDashboard.pendingTasksState.pendingTasks,
  );

  const first5CachedTasks = cachedTasks.slice(0, 5);

  const isAllowedAssignTasks = userPermissions['tasks_assign'];

  const elementRef = useRef<HTMLDivElement>(null);
  //@ts-expect-error works
  const visible = useIntersectionObserver(elementRef, { threshold: 0.5 });

  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        const first5CachedTasks = cachedTasks.slice(0, 5);
        const setRead = first5CachedTasks.map((task) => task.task_id);
        dispatch(staffDActions.setAsRead(setRead));
      }, 3000);
    }
  }, [visible]);

  return (
    <div
      ref={elementRef}
      className="flex size-full grow flex-col justify-between gap-1"
    >
      <div className="relative flex size-full grow flex-col gap-4">
        <div className="-mx-5 -mt-5 flex shrink-0 items-center justify-between gap-3 px-10 py-3 text-2xl font-semibold shadow-[0_2px_6px_-2px_rgba(0,0,0,0.5)]">
          {t('myTasks')}
          {isFetching ? (
            <i className="pi pi-spin pi-spinner"></i>
          ) : (
            <Chip
              className="size-8 shrink-0"
              template={
                <div className="grid size-full place-items-center">
                  {convertNumber(first5CachedTasks.length) + ''}
                </div>
              }
            />
          )}
        </div>
        <RemoveScroll className="flex grow flex-col text-lg">
          <ScrollPanel style={{ height: '330px' }}>
            {first5CachedTasks.map((task, i) => {
              const assignedOn = task.assigned_on;
              const isAssignedOnToday = isToday(new Date(assignedOn));
              const time = format(new Date(assignedOn), 'hh:mm a');
              const dateReadable = format(new Date(assignedOn), 'MMM dd, yyyy');
              return (
                <div
                  key={task.task_id}
                  className={cn(
                    'flex items-center justify-between gap-1 border-b',
                    i === cachedTasks.length - 1 && 'border-transparent',
                  )}
                >
                  <div className="flex gap-1">
                    <div className="w-[30px] shrink-0">
                      <i
                        className={cn(
                          'pi ml-3 mt-5 text-xs',
                          task.isNew
                            ? 'pi-circle-fill text-indigo-500'
                            : 'pi-check text-surface-400',
                        )}
                      />
                    </div>
                    <div className={cn('flex flex-col py-3')}>
                      <div className="font-medium text-surface-900">
                        {task.description}
                      </div>
                      <div className="text-surface-400">
                        {isAssignedOnToday ? time : dateReadable}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </ScrollPanel>
        </RemoveScroll>
      </div>
      <div className="-mx-5 -mb-5 h-16 shrink-0 shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.5)]">
        <div className="flex size-full items-center justify-between">
          <Link
            onClick={(e) => {
              overlayRef.current?.toggle(e);
            }}
            to={'/main/dashboard?t=0'}
          >
            <Button
              type="button"
              severity="info"
              className="h-10 w-fit text-indigo-500 shadow-none shadow-indigo-500"
              text
            >
              {t('viewAllTasks')}
            </Button>
          </Link>
          {isAllowedAssignTasks && (
            <Link
              onClick={(e) => {
                overlayRef.current?.toggle(e);
              }}
              to={'/main/dashboard?t=1'}
            >
              <Button
                type="button"
                severity="info"
                className="h-10 w-fit text-indigo-500 shadow-none shadow-indigo-500"
                text
              >
                {t('viewAllAssignedTasks')}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
